// tabs/MembersTab.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableContainer,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  InputAdornment,
  FormControl,
  Select,
  Avatar,
  TextField,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PersonRemove as PersonRemoveIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Block as BlockIcon,
  FilterList as FilterIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';

import { API_BASE_URL } from 'config/api';
import {
  SearchBar,
  StyledTableCell,
  StyledTableRow,
  MemberAvatar,
  RoleBadge,
  StatusBadge,
  ActionButton,
  EmptyState,
  honeyColors,
} from '../styled';

const MembersTab = ({
  members,
  allMembers,
  filters,
  saving,
  onFilterChange,
  onMemberAction,
  onInviteMember,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', member: null });
  const [roleDialog, setRoleDialog] = useState({ open: false, member: null, newRole: '' });
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('PA');
  const [inviteMessage, setInviteMessage] = useState('');

  const handleInviteOpen = () => setInviteDialogOpen(true);
  const handleInviteClose = () => {
    setInviteDialogOpen(false);
    setInviteEmail('');
    setInviteRole('PA');
    setInviteMessage('');
  };

  const handleInviteSubmit = () => {
    if (onInviteMember && inviteEmail) {
      onInviteMember({
        email: inviteEmail,
        role: inviteRole,
        personal_message: inviteMessage,
      });
      handleInviteClose();
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  const getRoleText = (role) => {
    const map = { 'FO': 'Founder', 'AD': 'Admin', 'MA': 'Manager', 'PT': 'Partner', 'EO': 'Event Organizer', 'PA': 'Participant', 'GU': 'Guest' };
    return map[role] || role;
  };

  const getStatusText = (status) => {
    const map = { 'AC': 'Active', 'PP': 'Pending', 'IN': 'Inactive', 'RE': 'Rejected', 'BA': 'Banned', 'DL': 'Deleted' };
    return map[status] || status;
  };

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const handleAction = (action) => {
    handleMenuClose();
    
    if (action === 'change_role') {
      setRoleDialog({ open: true, member: selectedMember, newRole: selectedMember.role });
    } else if (['remove', 'ban', 'reject'].includes(action)) {
      setConfirmDialog({ open: true, action, member: selectedMember });
    } else {
      onMemberAction(selectedMember.slug, action);
    }
  };

  const handleConfirmAction = () => {
    onMemberAction(confirmDialog.member.slug, confirmDialog.action);
    setConfirmDialog({ open: false, action: '', member: null });
  };

  const handleRoleChange = () => {
    onMemberAction(roleDialog.member.slug, 'change_role', { new_role: roleDialog.newRole });
    setRoleDialog({ open: false, member: null, newRole: '' });
  };

  // Stats
  const stats = {
    total: allMembers.length,
    active: allMembers.filter(m => m.status === 'AC').length,
    pending: allMembers.filter(m => m.status === 'PP').length,
    inactive: allMembers.filter(m => ['IN', 'RE', 'BA'].includes(m.status)).length,
  };

  return (
    <Box>
      {/* Filters */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <SearchBar
          size="small"
          placeholder="Search members..."
          value={filters.search}
          onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: honeyColors.textMuted }} />
              </InputAdornment>
            ),
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filters.status}
            onChange={(e) => onFilterChange({ ...filters, status: e.target.value })}
            displayEmpty
            sx={{
              borderRadius: '12px',
              backgroundColor: honeyColors.surface,
              border: `1px solid ${honeyColors.border}`,
              '& fieldset': { border: 'none' },
            }}
          >
            <MenuItem value="all">All Status ({stats.total})</MenuItem>
            <MenuItem value="AC">Active ({stats.active})</MenuItem>
            <MenuItem value="PP">Pending ({stats.pending})</MenuItem>
            <MenuItem value="IN">Inactive</MenuItem>
            <MenuItem value="BA">Banned</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <Select
            value={filters.role}
            onChange={(e) => onFilterChange({ ...filters, role: e.target.value })}
            displayEmpty
            sx={{
              borderRadius: '12px',
              backgroundColor: honeyColors.surface,
              border: `1px solid ${honeyColors.border}`,
              '& fieldset': { border: 'none' },
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            <MenuItem value="FO">Founders</MenuItem>
            <MenuItem value="AD">Admins</MenuItem>
            <MenuItem value="MA">Managers</MenuItem>
            <MenuItem value="PA">Participants</MenuItem>
            <MenuItem value="GU">Guests</MenuItem>
          </Select>
        </FormControl>

        {/* Invite Member Button */}
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleInviteOpen}
          sx={{
            borderRadius: '12px',
            backgroundColor: honeyColors.primary,
            color: '#fff',
            fontWeight: 600,
            textTransform: 'none',
            px: 3,
            '&:hover': {
              backgroundColor: honeyColors.primaryDark,
            },
          }}
        >
          Invite Member
        </Button>
      </Box>

      {/* Quick Stats */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {[
          { label: 'Total', value: stats.total, color: honeyColors.textPrimary },
          { label: 'Active', value: stats.active, color: honeyColors.success },
          { label: 'Pending', value: stats.pending, color: honeyColors.warning },
          { label: 'Inactive', value: stats.inactive, color: honeyColors.textMuted },
        ].map((stat) => (
          <Chip
            key={stat.label}
            label={`${stat.label}: ${stat.value}`}
            sx={{
              backgroundColor: `${stat.color}15`,
              color: stat.color,
              fontWeight: 600,
            }}
          />
        ))}
      </Box>

      {/* Members Table */}
      {members.length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: '16px',
            border: `1px solid ${honeyColors.border}`,
            boxShadow: 'none',
          }}
        >
          <Table>
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>Member</StyledTableCell>
                <StyledTableCell>Email</StyledTableCell>
                <StyledTableCell>Role</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Joined</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <StyledTableRow key={member.slug}>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <MemberAvatar src={getImageUrl(member.user?.avatar)}>
                        {member.user?.first_name?.charAt(0) || '?'}
                      </MemberAvatar>
                      <Box>
                        <Typography fontWeight={600}>
                          {member.user?.first_name || ''} {member.user?.last_name || ''}
                        </Typography>
                        {member.user?.work_headline && (
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                            {member.user.work_headline}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2">{member.user?.email}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <RoleBadge role={member.role} label={getRoleText(member.role)} />
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge status={member.status} label={getStatusText(member.status)} size="small" />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography variant="body2" color="textSecondary">
                      {dayjs(member.create_date).format('MMM D, YYYY')}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, member)}
                      disabled={saving}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState>
          <PersonIcon sx={{ fontSize: 64, color: honeyColors.border, mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            No members found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Try adjusting your filters
          </Typography>
        </EmptyState>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {selectedMember?.status === 'PP' && (
          <>
            <MenuItem onClick={() => handleAction('approve')}>
              <ListItemIcon><CheckCircleIcon sx={{ color: honeyColors.success }} /></ListItemIcon>
              <ListItemText>Approve Member</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleAction('reject')}>
              <ListItemIcon><CancelIcon sx={{ color: honeyColors.error }} /></ListItemIcon>
              <ListItemText>Reject Request</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}
        
        <MenuItem onClick={() => handleAction('change_role')}>
          <ListItemIcon><AdminIcon /></ListItemIcon>
          <ListItemText>Change Role</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => window.open(`mailto:${selectedMember?.user?.email}`)}>
          <ListItemIcon><EmailIcon /></ListItemIcon>
          <ListItemText>Send Email</ListItemText>
        </MenuItem>
        
        <Divider />
        
        {selectedMember?.status !== 'BA' && (
          <MenuItem onClick={() => handleAction('ban')} sx={{ color: honeyColors.warning }}>
            <ListItemIcon><BlockIcon sx={{ color: honeyColors.warning }} /></ListItemIcon>
            <ListItemText>Ban Member</ListItemText>
          </MenuItem>
        )}
        
        <MenuItem onClick={() => handleAction('remove')} sx={{ color: honeyColors.error }}>
          <ListItemIcon><PersonRemoveIcon sx={{ color: honeyColors.error }} /></ListItemIcon>
          <ListItemText>Remove Member</ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, action: '', member: null })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {confirmDialog.action === 'remove' && 'Remove Member'}
          {confirmDialog.action === 'ban' && 'Ban Member'}
          {confirmDialog.action === 'reject' && 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {confirmDialog.action}{' '}
            <strong>
              {confirmDialog.member?.user?.first_name} {confirmDialog.member?.user?.last_name}
            </strong>
            ?
          </Typography>
          {confirmDialog.action === 'ban' && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              This member will be banned from the community and cannot rejoin.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, action: '', member: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color="error"
            variant="contained"
          >
            {confirmDialog.action === 'remove' && 'Remove'}
            {confirmDialog.action === 'ban' && 'Ban'}
            {confirmDialog.action === 'reject' && 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog
        open={roleDialog.open}
        onClose={() => setRoleDialog({ open: false, member: null, newRole: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Member Role</DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} mb={3} mt={1}>
            <Avatar src={getImageUrl(roleDialog.member?.user?.avatar)}>
              {roleDialog.member?.user?.first_name?.charAt(0)}
            </Avatar>
            <Box>
              <Typography fontWeight={600}>
                {roleDialog.member?.user?.first_name} {roleDialog.member?.user?.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current role: {getRoleText(roleDialog.member?.role)}
              </Typography>
            </Box>
          </Box>
          
          <FormControl fullWidth>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              New Role
            </Typography>
            <Select
              value={roleDialog.newRole}
              onChange={(e) => setRoleDialog({ ...roleDialog, newRole: e.target.value })}
            >
              <MenuItem value="FO">Founder</MenuItem>
              <MenuItem value="AD">Admin</MenuItem>
              <MenuItem value="MA">Manager</MenuItem>
              <MenuItem value="PA">Participant</MenuItem>
              <MenuItem value="GU">Guest</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, member: null, newRole: '' })}>
            Cancel
          </Button>
          <Button
            onClick={handleRoleChange}
            variant="contained"
            sx={{ backgroundColor: honeyColors.primary, color: honeyColors.textPrimary }}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Member Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleInviteClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          Invite Member
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter email address"
              required
            />
            
            <FormControl fullWidth>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Role
              </Typography>
              <Select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <MenuItem value="PA">Participant</MenuItem>
                <MenuItem value="GU">Guest</MenuItem>
                <MenuItem value="MA">Manager</MenuItem>
                <MenuItem value="AD">Admin</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Personal Message (Optional)"
              multiline
              rows={3}
              fullWidth
              value={inviteMessage}
              onChange={(e) => setInviteMessage(e.target.value)}
              placeholder="Add a personal message to the invitation email..."
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleInviteClose}>
            Cancel
          </Button>
          <Button
            onClick={handleInviteSubmit}
            variant="contained"
            disabled={!inviteEmail}
            sx={{ 
              backgroundColor: honeyColors.primary, 
              color: '#fff',
              '&:hover': {
                backgroundColor: honeyColors.primaryDark,
              },
            }}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MembersTab;
